import React from 'react';

import ActionButton from '~components/atoms/ActionButton';
import Table from '~components/molecules/Table';
import Text from '~components/atoms/Text';
import UtilFunctions from '~utils/functions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { AdminSettingsChangeTypeEnum, FundTypeEnum, ModuleEnum, PermissionsEnum, TransactionStatusEnum } from '~utils/enums';

import * as Styles from './_productApprovalTableStyles.module.scss';
import { TUpdateDiscountStatusReduxParams } from '~types/feat/approval';
import combinedActions from '~redux/combinedActions';

const ProductApprovalTable = () => {
    const dispatch = useAppDispatch();

    const {
        Misc:
        {
            dateTimeFormat,
            renderTransactionStatusChip,
        },
    } = UtilFunctions;

    const pendingProductListing = useAppSelector(state => state.approval.pendingProductListing.data);
    const getPendingProductIsLoading = useAppSelector(state => state.approval.loadings.getPendingProductListing);

    const allProducts = useAppSelector(state => state.gold.allProducts);

    const isMarketOpen = useAppSelector(state => state.auth.isMarketOpen);
    const access = useAppSelector(state => state.auth.userDetails.access);
    const hasEditAccess = access.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.Approval);

    const setTempUpdateTransactionState = (data: TUpdateDiscountStatusReduxParams) => dispatch(combinedActions.approvalSetTempUpdateProductState(data));
    const setConfirmUpdateStatusModalIsOpen = (open: boolean) => dispatch(combinedActions.approvalSetConfirmUpdateProductStatusModalIsOpen(open));

    const handleApproveClick = (data: TUpdateDiscountStatusReduxParams) => {
        setConfirmUpdateStatusModalIsOpen(true);
        setTempUpdateTransactionState(data);
    };

    const handleRejectClick = (data: TUpdateDiscountStatusReduxParams) => {
        setConfirmUpdateStatusModalIsOpen(true);
        setTempUpdateTransactionState(data);
    };

    const tableHeader = [
        'PRODUCT ID',
        'TRANSACTION TYPE',
        'PRODUCT NAME',
        'FUND TYPE',
        'GOLD PRICE BANK BUY',
        'GOLD PRICE BANK SELL',
        'SILVER PRICE BANK BUY',
        'SILVER PRICE BANK SELL',
        'AFFIN BANK BUY SPLIT',
        'AFFIN BANK SELL SPLIT',
        'TTT BANK BUY SPLIT',
        'TTT BANK SELL SPLIT',
        'MINTING COST',
        'DATE',
        'STATUS',
        'ACTION',
    ];

    const renderActionButton = (id: number, status:TransactionStatusEnum ) => {
        if (status === TransactionStatusEnum.Pending){
            return (
                <div
                    className={Styles['action-button-container']}
                >
                    {hasEditAccess
                    && (
                        <ActionButton
                            label='Approve'
                            onClick={() => handleApproveClick({ id, status: TransactionStatusEnum.Approved })}
                            className={`${Styles['action-button']} ${Styles.approve}`}
                        />
                    )
                    }

                    {hasEditAccess
                    && (
                        <ActionButton
                            label='Reject'
                            onClick={() => handleRejectClick({ id, status: TransactionStatusEnum.Rejected })}
                            className={`${Styles['action-button']} ${Styles.reject}`}
                        />
                    )
                    }
                </div>
            );
        }

        return '-';

    };

    const tableBody = pendingProductListing.map((item, index) => {
        const {
            id,
            changeType,
            productId,
            productName,
            fundType,
            fixedBankBuyGoldPrice,
            fixedBankSellGoldPrice,
            fixedBankBuySilverPrice,
            fixedBankSellSilverPrice,
            affinBankBuySplit,
            affinBankBuySplitPercentage,
            tttBankBuySplit,
            tttBankBuySplitPercentage,
            tttBankSellSplit,
            tttBankSellSplitPercentage,
            affinBankSellSplit,
            affinBankSellSplitPercentage,
            mintingCost,
            mintingCostPercentage,
            status,
            updatedAt,
        } = item;

        const handleCalculateValue = (fixedValue: string | undefined, percentageValue: string | undefined) => {
            if (fixedValue){
                const shifted = ((parseFloat(fixedValue) * 100) / 100);

                if (Number.isInteger(shifted * 100)) {
                    return `RM${shifted.toFixed(2)}`;
                }

                return `RM${parseFloat(shifted.toFixed(6)).toString()}`;
            }

            if (percentageValue){
                const shifted = Math.round(Number(percentageValue) * 100);

                if (Number.isInteger(shifted * 100)) {
                    return shifted.toFixed(2).concat('%');
                }

                return parseFloat(shifted.toFixed(6)).toString().concat('%');
            }

            return '-';
        };

        const renderTransactionType = () => {
            switch (changeType) {
                case AdminSettingsChangeTypeEnum.Create:

                    return (<Text label='PRODUCT CREATION' className={Styles['discount-type-date']} />);
                case AdminSettingsChangeTypeEnum.Edit:

                    return (<Text label='PRODUCT EDITING' className={Styles['discount-type-date']} />);
                case AdminSettingsChangeTypeEnum.Delete:

                    return (<Text label='PRODUCT DELETION' className={Styles['discount-type-date']} />);
                default:
                    return '-';
            }
        };

        return [
            productId,
            renderTransactionType(),
            productName,
            FundTypeEnum[fundType],
            fixedBankBuyGoldPrice ? `RM${((Number(fixedBankBuyGoldPrice) * 100) / 100).toFixed(2)}` : 'API',
            fixedBankSellGoldPrice ? `RM${((Number(fixedBankSellGoldPrice) * 100) / 100).toFixed(2)}` : 'API',
            fixedBankBuySilverPrice ? `RM${((Number(fixedBankBuySilverPrice) * 100) / 100).toFixed(2)}` : 'API',
            fixedBankSellSilverPrice ? `RM${((Number(fixedBankSellSilverPrice) * 100) / 100).toFixed(2)}` : 'API',
            handleCalculateValue(affinBankBuySplit, affinBankBuySplitPercentage),
            handleCalculateValue(affinBankSellSplit, affinBankSellSplitPercentage),
            handleCalculateValue(tttBankBuySplit, tttBankBuySplitPercentage),
            handleCalculateValue(tttBankSellSplit, tttBankSellSplitPercentage),
            handleCalculateValue(mintingCost, mintingCostPercentage),
            (
                <div
                    key={index}
                    className={Styles['discount-type-date']}
                >
                    {dateTimeFormat(updatedAt)}
                </div>
            ),
            renderTransactionStatusChip(status, TransactionStatusEnum[status]),
            renderActionButton(id, status),
        ];
    });

    return (
        <Table
            header={tableHeader}
            body={tableBody}
            isLoading={getPendingProductIsLoading}
        />
    );
};

export default ProductApprovalTable;
